// @ts-nocheck
// Fill the whole builder from a JSON export: paste it, or drop a .json file.
// Everything it fills stays editable afterwards — this is a shortcut, not a lock.
import { useRef, useState } from 'react';
import { AlertTriangle, FileJson, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { parseAgentJson } from './agentPayload';

export default function ImportJsonDialog({ open, onOpenChange, onImport, options }) {
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const reset = () => {
    setText('');
    setFileName('');
    setError('');
    setPreview(null);
  };

  const check = (value) => {
    setText(value);
    setError('');
    setPreview(null);
    if (!value.trim()) return;
    try {
      setPreview(parseAgentJson(value, options));
    } catch (err) {
      setError(err.message);
    }
  };

  const readFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    check(await file.text());
  };

  const apply = () => {
    if (!preview) return;
    onImport(preview);
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import agent JSON</DialogTitle>
          <DialogDescription>
            Fills every field from an exported agent. You can edit anything afterwards — nothing is
            saved until you publish.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              readFile(e.dataTransfer.files?.[0]);
            }}
            className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border bg-muted/30 px-4 py-6 text-center"
          >
            <FileJson className="h-5 w-5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Drop a <span className="font-medium text-foreground">.json</span> file here
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => readFile(e.target.files?.[0])}
            />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
              <Upload className="h-4 w-4" /> Choose file
            </Button>
            {fileName && <p className="text-xs text-muted-foreground">{fileName}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="agent-json" className="text-sm">
              …or paste the JSON
            </Label>
            <Textarea
              id="agent-json"
              rows={8}
              value={text}
              spellCheck={false}
              placeholder='{ "agent_name": "Hotel Bot", "voice_id": 7, ... }'
              onChange={(e) => check(e.target.value)}
              className="font-mono text-xs"
            />
          </div>

          {error && (
            <p className="flex gap-2 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-2.5 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
            </p>
          )}

          {preview && (
            <div className="rounded-xl border border-border bg-card/70 p-4 text-sm">
              <p className="font-medium text-foreground">
                Ready to fill: {preview.state.agentName}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {preview.state.instructions.length} instruction(s) ·{' '}
                {preview.state.functions.length} function(s) ·{' '}
                {preview.state.laya.answers.length} fast answer(s) ·{' '}
                {preview.state.completionRules.length} completion rule(s)
              </p>
              {preview.warnings.length > 0 && (
                <ul className="mt-2 list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
                  {preview.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!preview} onClick={apply}>
            Fill the form
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
